import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { of } from 'rxjs';
import { catchError, take } from 'rxjs/operators';

import { Store } from '@ngrx/store';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BillingService } from 'src/app/core/services/billing';
import { BillingPreferences } from 'src/app/models/billing-preferences.model';
import { BillingSummary } from 'src/app/models/billing-summary.model';
import { Invoice } from 'src/app/models/invoice.model';
import { Payment } from '../../../models/payment.model';
import { Permission } from 'src/app/shared/constants/permissions';
import { ToastNotificationComponent } from 'src/app/shared/components/toast-notification/toast-notification.component';
import * as fromUserSelectors from 'src/app/store/selectors/users.selector';

@Component({
  selector: 'app-billing-overview',
  templateUrl: './billing-overview.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BillingOverviewComponent {
  readonly Permission = Permission;

  readonly summary = input<BillingSummary | null>(null);
  readonly payments = input<Payment[]>([]);
  readonly isPaymentsLoading = input<boolean>(false);
  readonly isPaymentsLoaded = input<boolean>(false);
  readonly invoices = input<Invoice[]>([]);
  readonly isInvoicesLoading = input<boolean>(false);

  readonly activeTab = signal<'invoices' | 'transactions'>('invoices');
  readonly isPaymentLinkLoading = signal(false);
  readonly isViewBillLoading = signal(false);
  readonly selectedInvoice = signal<Invoice | null>(null);
  readonly pdfUrl = signal<string | null>(null);
  readonly isLoadingPdf = signal(false);

  private readonly store = inject(Store);
  private readonly billingService = inject(BillingService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  readonly currentUser = toSignal(this.store.select(fromUserSelectors.getCurrentUser));
  readonly preferences = toSignal(
    this.billingService.getPreferences().pipe(catchError(() => of(null as BillingPreferences | null))),
    { initialValue: null as BillingPreferences | null },
  );
  readonly safePdfUrl = computed(() => {
    const url = this.pdfUrl();
    return url ? this.sanitizer.bypassSecurityTrustResourceUrl(`${url}#navpanes=0&zoom=100`) : null;
  });

  constructor() {
    effect(() => {
      const invoices = this.invoices();
      const selected = this.selectedInvoice();

      if (selected && !invoices.some((inv) => inv.Id === selected.Id)) {
        this.selectedInvoice.set(null);
        this.pdfUrl.set(null);
        this.isLoadingPdf.set(false);
        return;
      }

      if (invoices.length > 0 && !selected) {
        const invoiceIdFromRoute = this.route.snapshot.queryParamMap.get('invoiceId');
        const invoiceFromRoute = invoiceIdFromRoute
          ? invoices.find((inv) => inv.Id === invoiceIdFromRoute)
          : null;
        const invoiceWithFile =
          invoiceFromRoute ??
          invoices.find((inv) => inv.file?.ContentDocument?.LatestPublishedVersionId);
        if (invoiceWithFile) {
          this.onInvoiceSelected(invoiceWithFile);
        }
      }
    });
  }

  onPayClick(): void {
    this.isPaymentLinkLoading.set(true);
    this.billingService
      .getPaymentLink()
      .pipe(take(1))
      .subscribe({
        next: ({ paymentUrl }) => {
          this.isPaymentLinkLoading.set(false);
          window.open(paymentUrl, '_blank');
        },
        error: (err) => {
          this.isPaymentLinkLoading.set(false);
          const message =
            err?.status === 404
              ? 'Payment link unavailable. Please contact support.'
              : 'Something went wrong. Please try again.';
          this.showToast(message, 'error');
        },
      });
  }

  onViewBill(): void {
    const versionId = this.summary()?.latestInvoiceContentVersionId;
    if (!versionId) return;

    this.isViewBillLoading.set(true);
    this.billingService
      .getInvoiceBlobById(versionId)
      .pipe(take(1))
      .subscribe({
        next: ({ url }) => {
          this.isViewBillLoading.set(false);
          window.open(url, '_blank');
        },
        error: () => {
          this.isViewBillLoading.set(false);
          this.showToast('Unable to load bill. Please try again.', 'error');
        },
      });
  }

  onTabKeydown(event: KeyboardEvent, current: 'invoices' | 'transactions'): void {
    let next: 'invoices' | 'transactions' | null = null;
    if (event.key === 'ArrowRight' || event.key === 'End') next = 'transactions';
    else if (event.key === 'ArrowLeft' || event.key === 'Home') next = 'invoices';
    if (next === null) return;
    event.preventDefault();
    this.activeTab.set(next);
    const id = next === 'invoices' ? 'billing-invoices-tab' : 'billing-transactions-tab';
    (document.getElementById(id) as HTMLElement)?.focus();
  }

  onInvoiceSelected(invoice: Invoice): void {
    const versionId = invoice.file?.ContentDocument?.LatestPublishedVersionId;
    if (!versionId) return;

    this.selectedInvoice.set(invoice);
    this.isLoadingPdf.set(true);

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { invoiceId: invoice.Id },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });

    this.billingService
      .getInvoiceBlobById(versionId)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ url }) => {
          this.isLoadingPdf.set(false);
          this.pdfUrl.set(url);
        },
        error: () => {
          this.isLoadingPdf.set(false);
          this.showToast('Unable to load PDF. Please try again.', 'error');
        },
      });
  }

  formatBillingCycle(value: string | null): string {
    if (!value) return '';
    const n = parseInt(value, 10);
    if (isNaN(n)) return value;
    const suffix = n === 11 || n === 12 || n === 13 ? 'th' : n % 10 === 1 ? 'st' : n % 10 === 2 ? 'nd' : n % 10 === 3 ? 'rd' : 'th';
    return `${n}${suffix} of the month`;
  }

  private showToast(message: string, type: 'success' | 'error'): void {
    this.snackBar.openFromComponent(ToastNotificationComponent, {
      duration: 7000,
      panelClass: ['toast-panel'],
      data: { message, type },
      horizontalPosition: 'center',
      verticalPosition: 'top',
    });
  }
}
